<template>
  <div class="trip360-page">
    <div class="t360p-head">
      <div class="t360p-head-info">
        <span class="t360p-key">{{ pageKey }}</span>
        <span
          v-if="trip?.status_name"
          class="t360-status-pill"
          :class="trip360StatusClass(trip.status_name)"
        >{{ trip.status_name }}</span>
        <span v-if="trip?.uid" class="t360p-uid" title="UID — связующий ключ карточки">{{ trip.uid }}</span>
        <span v-if="trip?.display_name" class="t360p-name">{{ trip.display_name }}</span>
      </div>
      <div class="t360p-head-actions">
        <button
          v-if="mode === MODE_FULL"
          type="button"
          class="t360p-icon-btn"
          title="Компактный режим"
          @click="modeOverride = MODE_COMPACT"
        >Б</button>
        <button
          v-else
          type="button"
          class="t360p-icon-btn"
          title="Полный режим"
          @click="modeOverride = MODE_FULL"
        >П</button>
        <button type="button" class="t360p-icon-btn" title="Обновить данные" @click="load">⟳</button>
      </div>
    </div>
    <div class="t360p-body">
      <Trip360Content :trip="trip" :loading="loading" :error="error" :mode="mode" />
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import Trip360Content from '@/components/trip360/Trip360Content.vue'
import { fetchTrip360 } from '@/backend/trip360Api'
import {
  MODE_COMPACT,
  MODE_FULL,
  resolveMode,
  trip360StatusClass,
} from '@/components/trip360/trip360Logic.mjs'

// Standalone-страница 360-обзора: открывается по прямой ссылке
// /trip360/<ключ> (новое окно/вкладка из попапа или шаринг ссылки).
// Данные грузит независимо от глобального store попапа.
export default {
  name: 'Trip360Page',
  components: { Trip360Content },
  setup() {
    const route = useRoute()

    const trip = ref(null)
    const loading = ref(false)
    const error = ref(null)
    const modeOverride = ref(null)

    const pageKey = computed(() => {
      // vue-router уже декодирует param — дополнительное декодирование не нужно.
      const key = route.params.key
      return key == null ? '' : String(key)
    })

    let requestSeq = 0

    async function load() {
      const key = pageKey.value.trim()
      if (!key) return
      const seq = ++requestSeq
      loading.value = true
      error.value = null
      try {
        const data = await fetchTrip360(key)
        if (seq !== requestSeq) return
        trip.value = data
      } catch (e) {
        if (seq !== requestSeq) return
        trip.value = null
        const detail = e?.response?.data?.detail
        error.value = detail || 'Не удалось загрузить данные рейса'
      } finally {
        if (seq === requestSeq) loading.value = false
      }
    }

    watch(pageKey, () => {
      trip.value = null
      load()
    })

    onMounted(() => {
      load()
      window.addEventListener('resize', onResize)
    })
    onBeforeUnmount(() => {
      requestSeq++
      window.removeEventListener('resize', onResize)
    })

    // Режим на странице зависит от ширины окна браузера, оверрайд — до конца сессии.
    const winWidth = ref(typeof window === 'undefined' ? 0 : window.innerWidth)
    const onResize = () => {
      winWidth.value = window.innerWidth
    }

    const mode = computed(() => resolveMode(winWidth.value, modeOverride.value))

    return {
      MODE_COMPACT,
      MODE_FULL,
      pageKey,
      trip,
      loading,
      error,
      mode,
      modeOverride,
      load,
      trip360StatusClass,
    }
  },
}
</script>

<style scoped>
.trip360-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  background: #fff;
  overflow: hidden;
}

.t360p-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  flex: none;
}

.t360p-head-info {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
}

.t360p-key {
  font-weight: 700;
  font-size: 16px;
  color: #101828;
  white-space: nowrap;
}

.t360p-uid {
  font-size: 11px;
  font-weight: 600;
  color: #5925dc;
  background: #f4f0ff;
  border-radius: 8px;
  padding: 2px 8px;
  white-space: nowrap;
}

.t360p-name {
  font-size: 13px;
  color: #667085;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.t360p-head-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.t360p-icon-btn {
  min-width: 28px;
  height: 28px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #475467;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  padding: 0 7px;
}

.t360p-icon-btn:hover {
  background: #f2f4f7;
}

.t360p-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 14px 16px;
}
</style>
